import {
  useEffect, useState, useCallback,
} from 'react';
import mime from 'mime/lite';
import { v4 as uuid } from 'uuid';
import { getContext } from '@picsart/miniapps-sdk';

import errorLogger from 'services/errorLogger';
import useBatchData from 'pages/Batch/sections/Context/useBatchData';
import { BATCH_ACTIONS, BATCH_TOOLS } from 'pages/Batch/configs';
import analytics from 'pages/Batch/utils/analytics';

const useBatchDeeplinks = () => {
  const [batchImages, setBatchImages] = useState<string[]>([]);
  const [batchTool, setBatchTool] = useState('');
  const [batchApply, setBatchApply] = useState(false);

  const [fileNames, setFileNames] = useState<string[] | null>(null);
  const { acceptFiles, setActiveTool, allSelected, process, activeTool, data, setSelectedKeys } = useBatchData();

  useEffect(() => {
    getContext().handlers.onOpen(async () => {
      const deeplink = getContext().general.location.getState();

      const { params: { image, tool, apply } } = deeplink;
      const [toolName = ''] = Array.isArray(tool) ? tool : [tool];
      const [isToolApply = false] = Array.isArray(apply) ? apply.map(el => Boolean(el === 'true')) : [Boolean(apply === 'true')];

      image && setBatchImages(Array.isArray(image) ? image : [image]);
      toolName && setBatchTool(toolName);
      isToolApply && setBatchApply(isToolApply);

      // Because event-storm is peerDependency in miniapp-sdk-types.
      // @ts-ignore
      image && getContext().general.location.dispatch({
        params: {
          route: '/batch',
        },
        route: '/',
      });
    });
  }, []);

  const getImagesFromUrl = useCallback(async () => {
    try {
      const names: string[] = [];
      const images = await Promise.all(batchImages.map(i => fetch(i)));
      const blobs = await Promise.all(images.map(b => b.blob()));
      const files = blobs.map((blob: Blob) => {
        const extension = mime.getExtension(blob.type);
        const fileName = `${uuid()}.${extension}`;
        if (fileName) {
          names.push(fileName);
        }
        return new File([blob], fileName, {
          type: blob.type,
          lastModified: Date.now(),
        });
      });

      setFileNames(names);
      acceptFiles(files, []);
      setBatchImages([]);
    } catch (error) {
      errorLogger.trigger(error as Error, 'error');
    }
  }, [acceptFiles, batchImages]);

  useEffect(() => {
    if (activeTool && batchApply) {
      if (activeTool === BATCH_TOOLS.removeBG) {
        process(BATCH_ACTIONS.removeBackground, { });
      }

      setBatchApply(false);
    }
  // TODO: check the process function as it should be in the dependencies but changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTool, batchApply]);

  useEffect(() => {
    if (fileNames) {
      const keysToSelect = data.reduce((res: string[], item) => {
        const fileName = item.name;
        if (fileName && fileNames.includes(fileName)) {
          res.push(item.key);
        }
        return res;
      }, []);

      if (keysToSelect.length) {
        setSelectedKeys(keysToSelect);
        setFileNames(null);
      }
    }
  }, [data, fileNames, setSelectedKeys]);

  useEffect(() => {
    if (batchImages.length) {
      getImagesFromUrl();
    }
  }, [batchImages, getImagesFromUrl]);

  useEffect(() => {
    if (!batchImages.length && !fileNames && Object.values(BATCH_TOOLS).includes(batchTool as BATCH_TOOLS)) {
      setActiveTool(batchTool as BATCH_TOOLS);
      analytics.openTool(batchTool as BATCH_TOOLS, allSelected, batchImages);
      setBatchTool('');
    }
  }, [batchImages, batchTool, fileNames, setActiveTool, allSelected]);
};

export default useBatchDeeplinks;
